const SCORE_KEYS = [
  "human_writing",
  "template_language",
  "business_reasoning",
  "negotiation_insight",
  "emotional_accuracy",
  "customer_psychology",
  "future_usefulness",
  "writing_dna_similarity",
];

const WEAK_FEEDBACK = {
  human_writing: {
    reason: "This version reads more like a format than the sample profile's natural working voice.",
    suggestion: "Keep the structure, but use plainer phrasing and fewer field-like constructions.",
  },
  template_language: {
    reason: "The note relies more on fixed labels and CRM-style phrasing than the sample profile usually does.",
    suggestion: "Keep the useful structure while softening canned labels where the destination allows it.",
  },
  business_reasoning: {
    reason: "Compression removes some of the cause-and-effect behind the commercial situation.",
    suggestion: "Preserve one short clause that explains why the blocker changes the decision path.",
  },
  negotiation_insight: {
    reason: "The note captures the outcome but loses some of the trade-offs and decision dynamics behind it.",
    suggestion: "Keep one concise line on the active leverage, condition, or competing option.",
  },
  emotional_accuracy: {
    reason: "The note compresses some of the customer's stance, tension, or candor that was present in the call.",
    suggestion: "Preserve one short phrase about the customer's stance when it changes how the account should be handled.",
  },
  customer_psychology: {
    reason: "The decision conditions remain, but some of the customer's motivation and internal pressure are no longer explicit.",
    suggestion: "Keep the customer's decision condition or internal constraint in one compact clause.",
  },
  future_usefulness: {
    reason: "The note is readable now but leaves too little context for the next conversation.",
    suggestion: "Retain the owner, commitment, deadline, and unresolved decision condition.",
  },
  writing_dna_similarity: {
    reason: "The output departs from the demo sample profile's usual balance of directness, context, and natural voice.",
    suggestion: "Match the sample profile's phrasing density and preserve the same level of reasoning without adding filler.",
  },
};

const REWRITE_DELTAS = {
  default: {},
  shorter: {
    overall: -1,
    human_writing: 2,
    template_language: 2,
    business_reasoning: -4,
    negotiation_insight: -2,
    emotional_accuracy: -4,
    customer_psychology: -3,
    future_usefulness: 1,
    writing_dna_similarity: 2,
  },
  more_professional: {
    overall: 0,
    human_writing: -5,
    template_language: -4,
    business_reasoning: 3,
    negotiation_insight: 1,
    emotional_accuracy: -4,
    customer_psychology: -1,
    future_usefulness: 1,
    writing_dna_similarity: -3,
  },
  more_conversational: {
    overall: 0,
    human_writing: 4,
    template_language: 3,
    business_reasoning: -1,
    negotiation_insight: 0,
    emotional_accuracy: 4,
    customer_psychology: 3,
    future_usefulness: 0,
    writing_dna_similarity: 2,
  },
  more_detailed: {
    overall: 1,
    human_writing: -1,
    template_language: -1,
    business_reasoning: 3,
    negotiation_insight: 3,
    emotional_accuracy: 2,
    customer_psychology: 2,
    future_usefulness: 2,
    writing_dna_similarity: -3,
  },
};

const REWRITE_FIT_COPY = {
  shorter: "Rewrite effect: stronger concision and directness; some business and relationship context is compressed.",
  more_professional: "Rewrite effect: stronger formal business framing; less natural voice and emotional texture than the demo sample profile.",
  more_conversational: "Rewrite effect: stronger natural voice and interpersonal tone; slightly less structured business framing.",
  more_detailed: "Rewrite effect: richer reasoning and future context; more detail than the demo sample profile usually keeps.",
};

const clamp = (value) => Math.max(0, Math.min(100, Math.round(Number(value) || 0)));

function metricWithFeedback(key, score) {
  const normalized = clamp(score);
  const feedback = normalized < 70 ? WEAK_FEEDBACK[key] : null;
  return feedback ? { score: normalized, ...feedback } : { score: normalized };
}

export function buildDemoWritingDnaProfile({
  overall,
  human_writing,
  template_language,
  business_reasoning,
  negotiation_insight,
  emotional_accuracy,
  customer_psychology,
  future_usefulness,
  writing_dna_similarity,
  profileConfidence,
  sampleFit,
}) {
  const dnaScore = clamp(writing_dna_similarity);
  return {
    overall: clamp(overall),
    human_writing: metricWithFeedback("human_writing", human_writing),
    template_language: metricWithFeedback("template_language", template_language),
    business_reasoning: metricWithFeedback("business_reasoning", business_reasoning),
    negotiation_insight: metricWithFeedback("negotiation_insight", negotiation_insight),
    emotional_accuracy: metricWithFeedback("emotional_accuracy", emotional_accuracy),
    customer_psychology: metricWithFeedback("customer_psychology", customer_psychology),
    future_usefulness: metricWithFeedback("future_usefulness", future_usefulness),
    writing_dna_similarity: metricWithFeedback("writing_dna_similarity", dnaScore),
    dna_profile: {
      calibrated: true,
      maturity_label: "Developing profile",
      confidence: clamp(profileConfidence),
      sample_profile: true,
      sample_fit: sampleFit,
    },
    dna_alignment: {
      applied: true,
      before_match: clamp(dnaScore - 7),
      after_match: dnaScore,
    },
  };
}

export function demoWritingDnaForVariant(scoreProfiles, fallbackProfile, style, variant = "default") {
  const base = scoreProfiles?.[style] || fallbackProfile;
  if (!base) return null;

  const deltas = REWRITE_DELTAS[variant] || REWRITE_DELTAS.default;
  const next = {
    ...base,
    overall: clamp((base.overall || 0) + (deltas.overall || 0)),
    dna_profile: { ...(base.dna_profile || {}) },
    dna_alignment: { ...(base.dna_alignment || {}) },
  };

  SCORE_KEYS.forEach((key) => {
    const current = Number(base[key]?.score);
    if (!Number.isFinite(current)) return;
    next[key] = metricWithFeedback(key, current + (deltas[key] || 0));
  });

  const dnaScore = Number(next.writing_dna_similarity?.score);
  if (Number.isFinite(dnaScore)) {
    next.dna_alignment = {
      ...next.dna_alignment,
      applied: true,
      before_match: clamp(dnaScore - 7),
      after_match: clamp(dnaScore),
    };
  }

  if (variant !== "default" && REWRITE_FIT_COPY[variant]) {
    next.dna_profile.sample_fit = REWRITE_FIT_COPY[variant];
  }

  return next;
}
